import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { addBookmark, getBookmarkIds, removeBookmark } from '@/api';
import { haptic } from '@/platform';
import { readStored, writeStored } from '@/storage';

const CACHE = 'saved_questions';
const MIGRATED = 'saved_migrated';

function parseIds(value: string | null): string[] {
  if (!value) return [];
  try {
    const arr: unknown = JSON.parse(value);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

interface SavedState {
  ids: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  failed: string | null;
}

const SavedContext = createContext<SavedState>({
  ids: [],
  has: () => false,
  toggle: () => {},
  failed: null,
});

const FAILED_NOTE_MS = 4000;

export function useSaved(): SavedState {
  return useContext(SavedContext);
}

export function SavedProvider({ children }: { children: ReactNode }) {
  const [ids, setIds] = useState<string[]>(() => parseIds(readStored(CACHE)));
  const seq = useRef(new Map<string, number>());
  const intent = useRef(new Map<string, boolean>());

  useEffect(() => {
    let cancelled = false;
    const legacy = readStored(MIGRATED) !== '1' ? parseIds(readStored(CACHE)) : [];

    async function reconcile() {
      try {
        const uploads = await Promise.allSettled(legacy.map((id) => addBookmark(id)));
        const migrated = uploads.every((u) => u.status === 'fulfilled');
        const serverIds = await getBookmarkIds();
        if (cancelled) return;
        const base = new Set(migrated ? serverIds : [...serverIds, ...legacy]);
        for (const [id, saving] of intent.current) {
          if (saving) base.add(id);
          else base.delete(id);
        }
        const next = [...base];
        setIds(next);
        writeStored(CACHE, JSON.stringify(next));
        if (migrated) writeStored(MIGRATED, '1');
        // eslint-disable-next-line no-empty
      } catch {}
    }
    void reconcile();
    return () => {
      cancelled = true;
    };
  }, []);

  const writes = useRef(new Map<string, Promise<unknown>>());
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (failed === null) return;
    const timer = setTimeout(() => setFailed(null), FAILED_NOTE_MS);
    return () => clearTimeout(timer);
  }, [failed]);

  const value = useMemo<SavedState>(() => {
    const set = new Set(ids);

    function toggle(id: string) {
      const saving = !set.has(id);
      const next = saving ? [...ids, id] : ids.filter((x) => x !== id);
      setIds(next);
      setFailed(null);
      writeStored(CACHE, JSON.stringify(next));

      const n = (seq.current.get(id) ?? 0) + 1;
      seq.current.set(id, n);
      intent.current.set(id, saving);

      const before = writes.current.get(id) ?? Promise.resolve();
      const write = before.then(() => (saving ? addBookmark(id) : removeBookmark(id)));
      writes.current.set(
        id,
        write.catch(() => {}),
      );
      void write.catch(() => {
        if (seq.current.get(id) !== n) return;
        intent.current.delete(id);
        setIds((cur) => {
          if (cur.includes(id) !== saving) return cur;
          const reverted = saving ? cur.filter((x) => x !== id) : [...cur, id];
          writeStored(CACHE, JSON.stringify(reverted));
          return reverted;
        });
        setFailed(id);
        haptic.notify('error');
      });
    }

    return { ids, has: (id) => set.has(id), toggle, failed };
  }, [ids, failed]);

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}
